import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/tr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Loader2, 
  Plus,
  Building,
  Users,
  Phone,
  Clock,
  Calendar as CalendarIcon
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

moment.locale('tr');
const localizer = momentLocalizer(moment);

const messages = {
  today: 'Bugün',
  previous: 'Geri',
  next: 'İleri',
  month: 'Ay',
  week: 'Hafta',
  day: 'Gün',
  agenda: 'Ajanda',
  date: 'Tarih',
  time: 'Saat',
  event: 'Etkinlik',
  noEventsInRange: 'Bu aralıkta etkinlik yok.',
  showMore: (total) => `+${total} daha`
};

// Salon renkleri - her salon için farklı renk
const venueColors = [
  { bg: '#3b82f6', text: '#ffffff' }, // Mavi
  { bg: '#10b981', text: '#ffffff' }, // Yeşil
  { bg: '#f59e0b', text: '#ffffff' }, // Turuncu
  { bg: '#ef4444', text: '#ffffff' }, // Kırmızı
  { bg: '#8b5cf6', text: '#ffffff' }, // Mor
  { bg: '#ec4899', text: '#ffffff' }, // Pembe
  { bg: '#06b6d4', text: '#ffffff' }, // Cyan
  { bg: '#84cc16', text: '#ffffff' }, // Lime
];

export default function SalonCalendarPage() {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');

  // Calendar navigation handler - must return the setter for proper sync
  const handleNavigate = useCallback((newDate) => {
    return setCalendarDate(newDate);
  }, []);

  // Calendar view change handler
  const handleViewChange = useCallback((newView) => {
    return setCalendarView(newView);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Salonları ve müşterileri paralel yükle
      const [venuesRes, customersRes] = await Promise.all([
        fetch(`${API}/api/salon/venues`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/salon/customers`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (venuesRes.ok) {
        const venuesData = await venuesRes.json();
        setVenues(venuesData);
      }

      if (customersRes.ok) {
        const customersData = await customersRes.json();
        setCustomers(customersData);
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Salon için renk getir
  const getVenueColor = (venueId) => {
    const index = venues.findIndex(v => v.id === venueId);
    return venueColors[index % venueColors.length] || venueColors[0];
  };

  // Takvim etkinliklerini oluştur
  const events = useMemo(() => {
    return customers.map(customer => {
      const eventDate = new Date(customer.event_date);
      const color = getVenueColor(customer.venue_id);
      
      // Saat bilgisini parse et
      let startHour = 18, startMin = 0, endHour = 23, endMin = 0;
      if (customer.event_time_start) {
        const [h, m] = customer.event_time_start.split(':');
        startHour = parseInt(h);
        startMin = parseInt(m);
      }
      if (customer.event_time_end) {
        const [h, m] = customer.event_time_end.split(':');
        endHour = parseInt(h);
        endMin = parseInt(m);
      }

      const start = new Date(eventDate);
      start.setHours(startHour, startMin, 0);
      
      const end = new Date(eventDate);
      end.setHours(endHour, endMin, 0);

      return {
        id: customer.id,
        title: `${customer.venue_name || 'Salon'}: ${customer.bride_name} & ${customer.groom_name}`,
        start,
        end,
        resource: customer,
        color
      };
    });
  }, [customers, venues]);

  // Etkinlik stili
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color.bg,
        color: event.color.text,
        borderRadius: '4px',
        border: 'none',
        fontSize: '12px',
        padding: '2px 4px'
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
    setDialogOpen(true);
  };

  const handleSelectSlot = ({ start }) => {
    // Yeni rezervasyon sayfasına tarih ile git
    const year = start.getFullYear();
    const month = String(start.getMonth() + 1).padStart(2, '0');
    const day = String(start.getDate()).padStart(2, '0');
    navigate(`/salon/musteriler/yeni?date=${year}-${month}-${day}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="salon-calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Takvim</h1>
          <p className="text-muted-foreground mt-1">Tüm salonların rezervasyonları</p>
        </div>
        <Button onClick={() => navigate('/salon/musteriler/yeni')}>
          <Plus className="h-4 w-4 mr-2" />
          Yeni Rezervasyon
        </Button>
      </div>

      {/* Salon Renk Göstergesi */}
      {venues.length > 0 && (
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-muted-foreground">Salonlar:</span>
              {venues.map((venue, index) => {
                const color = venueColors[index % venueColors.length];
                return (
                  <div key={venue.id} className="flex items-center gap-2">
                    <div 
                      className="w-4 h-4 rounded" 
                      style={{ backgroundColor: color.bg }}
                    />
                    <span className="text-sm">{venue.name}</span>
                    <span className="text-xs text-muted-foreground">({venue.capacity} kişi)</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Takvim */}
      <Card>
        <CardContent className="p-4">
          <div style={{ height: '600px' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              messages={messages}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleSelectEvent}
              onSelectSlot={handleSelectSlot}
              selectable
              popup
              views={['month', 'week', 'day', 'agenda']}
              view={calendarView}
              date={calendarDate}
              onNavigate={handleNavigate}
              onView={handleViewChange}
              style={{ height: '100%' }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Etkinlik Detay Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              Rezervasyon Detayı
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-lg">
                    {selectedEvent.bride_name} & {selectedEvent.groom_name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(selectedEvent.event_date)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Salon</p>
                    <p className="font-medium">{selectedEvent.venue_name || '-'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Saat</p>
                    <p className="font-medium">
                      {selectedEvent.event_time_start} - {selectedEvent.event_time_end}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Telefon</p>
                    <p className="font-medium">{selectedEvent.phone1 || selectedEvent.bride_phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Davetli</p>
                    <p className="font-medium">{selectedEvent.guest_count || '-'} Kişi</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                <span className="text-sm text-muted-foreground">Toplam Tutar</span>
                <span className="text-xl font-bold text-primary">
                  {formatCurrency(selectedEvent.total_price || 0)}
                </span>
              </div>

              <div className="flex gap-2">
                <Button 
                  className="flex-1" 
                  onClick={() => {
                    setDialogOpen(false);
                    navigate(`/salon/musteriler/${selectedEvent.id}`);
                  }}
                >
                  Detaya Git
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                >
                  Kapat
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
